import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TutorialModal from '../components/TutorialModal';
import { Play, BookOpen, Home } from 'lucide-react';

/**
 * Public, no-auth tutorial page.
 * Wraps the existing in-game TutorialModal so Itch.io / first-time visitors
 * can preview the rules before signing up.
 */
export default function Tutorial() {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(true);

  const handleClose = () => setShowModal(false);
  const handleComplete = () => setShowModal(false);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-950 to-black relative overflow-hidden">
      {/* Cinematic backdrop */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="fixed inset-0 w-full h-full object-cover opacity-30"
        style={{ zIndex: 0 }}
      >
        <source src="/assets/world-leaders-nuke.mp4" type="video/mp4" />
      </video>
      <div className="absolute inset-0 bg-black/60" style={{ zIndex: 1 }} />

      {/* Tutorial modal (auto-shows on mount) */}
      {showModal && (
        <TutorialModal onClose={handleClose} onComplete={handleComplete} />
      )}

      {/* Post-tutorial / closed state — landing CTA */}
      {!showModal && (
        <div className="relative z-10 min-h-screen flex flex-col items-center justify-center p-6">
          <div className="max-w-2xl w-full bg-slate-900/90 backdrop-blur-md border-4 border-yellow-500/60 rounded-2xl p-10 text-center shadow-2xl"
               style={{ boxShadow: '0 0 50px rgba(234, 179, 8, 0.3)' }}>
            <div className="text-7xl mb-4 animate-pulse">☢</div>
            <h1 className="text-4xl font-bold text-yellow-400 mb-3 tracking-wider"
                style={{ fontFamily: "'Courier New', monospace" }}>
              Briefing Complete, Commander
            </h1>
            <p className="text-slate-300 text-lg mb-8">
              You now know the rules of <strong className="text-orange-400">Global Thermonuclear War 3</strong>.<br/>
              The world is waiting for your first move.
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <button
                data-testid="tutorial-play-btn"
                onClick={() => navigate('/')}
                className="flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 text-white rounded-lg font-bold text-lg transition-all border-2 border-orange-400 shadow-lg hover:scale-105"
                style={{ boxShadow: '0 0 30px rgba(255,107,53,0.4)' }}
              >
                <Play className="w-5 h-5" /> Launch Command Center
              </button>
              <button
                data-testid="tutorial-replay-btn"
                onClick={() => setShowModal(true)}
                className="flex items-center gap-2 px-6 py-4 bg-slate-800 hover:bg-slate-700 text-white rounded-lg font-semibold transition-all border border-slate-600"
              >
                <BookOpen className="w-5 h-5" /> Replay Tutorial
              </button>
            </div>

            <div className="mt-6 pt-6 border-t border-slate-700 text-sm text-slate-400">
              <a href="/game-mechanics-reference.html" className="hover:text-orange-400 mx-2">📊 Mechanics Docs</a>
              <span>·</span>
              <a href="/game-flow-reference.html" className="hover:text-orange-400 mx-2">📘 Game Flow</a>
              <span>·</span>
              <a href="/about.html" className="hover:text-orange-400 mx-2">📄 About</a>
            </div>
          </div>

          <button
            data-testid="tutorial-home-btn"
            onClick={() => navigate('/')}
            className="mt-6 flex items-center gap-2 px-4 py-2 text-slate-400 hover:text-white text-sm transition-all"
          >
            <Home className="w-4 h-4" /> Back to home
          </button>
        </div>
      )}
    </div>
  );
}
